import {
    CONDITION_TYPES,
    MAX_RULE_NAME_LENGTH,
    MISSING_OPERATOR,
    NEW_RULE_ID,
    validateNumber,
    validateText,
} from './constants';

export function getConditionType(dimension) {
    return CONDITION_TYPES.find(type => type.value === dimension);
}

export function findOption(options, value) {
    return options.find(option => option.value === value) || null;
}

// Open for editing = id present in the editing map with a defined draft.
export function isEditing(editing, id) {
    return id in editing && typeof editing[id] !== 'undefined';
}

export function validateRule(rule) {
    const invalids = [];
    const validName = !!rule.name;
    for (let i = 0; i < rule.conditions.length; i++) {
        const cond = rule.conditions[i];
        const type = getConditionType(cond.dimension);
        if (('hasValues' in type && type.hasValues === false) || cond.operator === MISSING_OPERATOR.value) continue;
        if (
            !cond.values ||
            (cond.values instanceof Array && cond.values.length === 0) ||
            (type.validate && !type.validate(cond))
        ) {
            invalids.push(i);
        } else if (type.number && !validateNumber(cond.values)) {
            invalids.push(i);
        } else if (type.text && !validateText(cond.values)) {
            invalids.push(i);
        }
    }
    return invalids.length || !validName ? { invalids, invalidName: !validName } : true;
}

export function markInvalid(rule, invalids, invalidName) {
    const conditions = rule.conditions.map((cond, i) => (invalids.includes(i) ? { ...cond, invalid: true } : cond));
    return { ...rule, conditions, invalidName };
}

export function applyConditionEdit(condition, key, value) {
    const next = { ...condition, [key]: value, invalid: false };
    if (key === 'dimension') {
        const type = getConditionType(value);
        const { operators } = type;
        next.operator = operators.length ? operators[0].value : null;
        next.customFieldName = undefined;
        next.values = [];
    }
    return next;
}

export function buildSavePayload(rule) {
    const conditions = rule.conditions.map(condition => ({
        dimension: condition.dimension,
        operator: condition.operator,
        values: condition.values,
        customFieldName: condition.customFieldName,
    }));
    return {
        id: rule.id === NEW_RULE_ID ? null : rule.id,
        active: rule.active,
        description: rule.name,
        action: rule.action,
        conditions,
    };
}

export function dedupeRuleName(name, existingNames) {
    const taken = candidate => existingNames.includes(candidate);
    let base = name;
    if (taken(base)) {
        let num = 1;
        const matches = /.* \((\d+)\)$/.exec(base);
        if (matches && matches.length > 1) {
            num = +matches[1] + 1;
            base = base.substring(0, base.length - 3 - (num - 1).toString().length);
        }
        while (taken(`${base} (${num})`)) {
            num++;
        }
        base = `${base} (${num})`;
    }
    return base.substring(0, MAX_RULE_NAME_LENGTH);
}

export function filterRules(rules, searchText) {
    if (searchText === undefined || searchText === '') return rules;
    const needle = searchText.toLowerCase();
    return rules.filter(rule => rule.name.toLowerCase().indexOf(needle) !== -1);
}

// A duplicated draft carries `placeUnder` so it renders directly below its source.
export function placeDuplicateRow(rows) {
    if (!rows.length || !rows[0].placeUnder) return rows;
    const result = rows.slice();
    const dupe = result.shift();
    const anchorIndex = result.findIndex(rule => rule.id === dupe.placeUnder);
    if (anchorIndex === -1) {
        result.unshift(dupe);
    } else {
        result.splice(anchorIndex + 1, 0, dupe);
    }
    return result;
}

// Header click cycles: none -> desc -> asc -> none.
export function nextSortState(currentKey, currentDir, key) {
    let dir;
    if (key !== currentKey) {
        dir = 'desc';
    } else if (currentDir === 'desc') {
        dir = 'asc';
    } else if (!currentDir) {
        dir = 'desc';
    }
    return dir === undefined ? { key: undefined, dir: undefined } : { key, dir };
}

export function sortRules(rules, column, dir) {
    if (!dir) return rules;
    const asc = dir === 'asc';
    const { sortKey, sortType } = column;
    // -1/0/1 comparator so number (updated) and boolean (active) columns reorder too.
    const compare = (a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (sortType === 'num') return (av || 0) - (bv || 0);
        if (av === bv) return 0;
        if (av === undefined || av === null) return -1;
        if (bv === undefined || bv === null) return 1;
        return av < bv ? -1 : 1;
    };
    return rules.slice().sort((a, b) => (asc ? compare(a, b) : -compare(a, b)));
}

// Admins outside an organization may not create/save a Reject rule.
export function isAdminSafeguarded({ disabled, rejectSaveDisabled, draft, savedRule }) {
    return (
        !disabled &&
        !!rejectSaveDisabled &&
        !!draft.dirty &&
        (draft.action === 0 || (savedRule && savedRule.action === 0))
    );
}
